import {
  TicketCancellation,
  TicketRefundCalculation,
  TicketRefundCalculationType,
  TicketRefundMethod,
} from 'types/shared/documents/restaurants/event';
import { TicketCancellationTemplate } from 'types/settings';

export type TicketRefundTotals = {
  paid: number;
  serviceFee: number;
  ticketValue: number;
  mollieRefundable: number;
  originalVoucher?: number;
};

export type TicketCancellationPayment = {
  id: string;
  paidVia: string;
  status: string;
  method: string | null;
  paidAt: string | null;
  amount: number;
  serviceFee: number;
  refunded: number;
  refundable: number;
  refundsUnavailable?: boolean;
  verified?: boolean;
};

/** Response of GET /v03/reservations/:rid/res/:id/ticketCancellation */
export type TicketCancellationPreview = {
  ticketId: string;
  reservationId: string;
  cancellation: TicketCancellation | null;
  operation?: { status: string; error: string | null; busy: boolean; requestedAmount: number; refundMethod: TicketRefundMethod; refundFailed: boolean; documentNumber: string | null; receiptNumbers: string[]; accountingTargets: { paymentId: string; amountCents: number }[] } | null;
  accounting?: { paymentId: string; allocationError: string; lines: { index: number; title: string; vatRate: number; remainingCents: number }[] }[];
  bookedAt: number | null;
  eventDate: string;
  eventTime: string;
  daysUntilEvent: number | null;
  mollieAvailable: boolean;
  hasPendingPayments: boolean;
  payments: TicketCancellationPayment[];
  totals: TicketRefundTotals;
};

const toCents = (euros: number | null | undefined) => {
  const value = Number(euros);

  return Number.isFinite(value) ? Math.round(value * 100) : 0;
};

/** The four ways to calculate a refund. Labels are keys of the `tickets` translations. */
export const CALCULATION_OPTIONS: {
  type: TicketRefundCalculationType;
  label: string;
}[] = [
  { type: 'full', label: 'Full amount' },
  { type: 'percentage', label: 'Percentage of the amount' },
  { type: 'fixed', label: 'Fixed amount' },
  { type: 'paidMinusFixed', label: 'Paid amount minus a fee' },
];

export const refundCalculationNeedsValue = (type: TicketRefundCalculationType) =>
  type !== 'full';

/**
 * Same rules as calculateRefundCents in gastronaut-api, so the dialog shows the
 * exact amount the API will refund.
 */
export function calculateRefundAmount(
  totals: TicketRefundTotals,
  calculation: TicketRefundCalculation
) {
  const base =
    toCents(totals.ticketValue) +
    (calculation.includeServiceFee ? toCents(totals.serviceFee) : 0);
  const value = Number(calculation.value ?? 0);

  switch (calculation.type) {
    case 'full':
      return base / 100;
    case 'percentage':
      return Math.round((base * Math.min(Math.max(value, 0), 100)) / 100) / 100;
    case 'fixed':
      return Math.min(toCents(value), toCents(totals.paid)) / 100;
    case 'paidMinusFixed':
      return Math.max(base - toCents(value), 0) / 100;
    default:
      return 0;
  }
}

/** Accepts "12,50" as well as "12.50". Empty or invalid input gives null. */
export function parseDecimalInput(input: string) {
  const text = input.trim().replace(',', '.');
  const value = Number(text);

  return /^\d+(?:[.,]\d{1,2})?$/.test(input.trim()) && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Refunding everything but the service fee leaves nothing to decide: every invoice line
 * but the fee, in full. The fee line keeps the name the payment gave it.
 */
export function allocationWithoutServiceFee(
  lines: { index: number; title: string; remainingCents: number }[],
  amountCents: number
) {
  const fees = lines.filter((line) => line.title === 'Service Fee' && line.remainingCents > 0);
  const total = lines.reduce((sum, line) => sum + line.remainingCents, 0);

  if (fees.length !== 1 || total - fees[0].remainingCents !== amountCents) return null;

  return lines
    .filter((line) => line !== fees[0] && line.remainingCents > 0)
    .map((line) => ({ index: line.index, amountCents: line.remainingCents }));
}

const PAYPAL_REFUND_DAYS = 180;

/** Why Mollie cannot refund: the PayPal window only when every such payment is a PayPal one past it. */
export function refundUnavailableHint(payments: TicketCancellationPayment[], now = Date.now()) {
  const expired = payments.filter(
    (payment) => payment.refundsUnavailable && payment.amount > payment.refunded
  );

  if (!expired.length) return 'none';

  return expired.every(
    (payment) =>
      payment.method === 'paypal' &&
      !!payment.paidAt &&
      now - Date.parse(payment.paidAt) > PAYPAL_REFUND_DAYS * 86_400_000
  )
    ? 'paypal'
    : 'general';
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** e.g. "50 % of the amount · incl. service fee · as a voucher" */
export function describeCancellationTemplate(
  template: Pick<TicketCancellationTemplate, 'calculation' | 'refundMethod'>,
  t: Translate,
  formatEuros: (amount: number) => string
) {
  const { type, value, includeServiceFee } = template.calculation;
  const amount =
    type === 'full'
      ? t('Full amount')
      : type === 'percentage'
      ? t('{{value}} % of the amount', { value: String(value ?? 0).replace('.', ',') })
      : type === 'fixed'
      ? t('{{value}} fixed', { value: formatEuros(value ?? 0) })
      : t('Paid amount minus {{value}}', { value: formatEuros(value ?? 0) });

  return [
    amount,
    includeServiceFee && type !== 'fixed' ? t('incl. service fee') : null,
    template.refundMethod === 'mollie' ? t('via Mollie') : template.refundMethod === 'originalVoucher' ? t('Restore the original voucher') : template.refundMethod === 'none' ? t('No refund') : t('as a voucher'),
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * The template with the highest "from N days before the event" that still
 * applies, e.g. 10 days before the event picks "from 7 days" over "from 0 days".
 */
export function suggestCancellationTemplate(
  templates: TicketCancellationTemplate[],
  daysUntilEvent: number | null
) {
  if (daysUntilEvent === null) return null;

  return (
    templates
      .filter(
        (template) =>
          typeof template.minDaysBeforeEvent === 'number' &&
          template.minDaysBeforeEvent <= daysUntilEvent
      )
      .sort(
        (a, b) => (b.minDaysBeforeEvent ?? 0) - (a.minDaysBeforeEvent ?? 0)
      )[0] ?? null
  );
}

/**
 * The template the cancel dialog starts with: the one suggested by the days
 * until the event, or the restaurant's only template if it has no day rule.
 * Without one the user has to pick a template or continue without one.
 */
export function initialCancellationTemplate(
  templates: TicketCancellationTemplate[],
  daysUntilEvent: number | null
) {
  const suggested = suggestCancellationTemplate(templates, daysUntilEvent);

  if (suggested) return suggested;

  const [onlyTemplate] = templates;

  return templates.length === 1 && typeof onlyTemplate.minDaysBeforeEvent !== 'number'
    ? onlyTemplate
    : null;
}
